import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { refundTransaction as refundTransactionApi } from 'api/transactions';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { useForceRender } from 'customHooks/useForceRender';
import { queryClient } from 'queries/queryClient';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface RefundTransactionDialogProps extends AlertDialog.AlertDialogRootProps {
    onSuccess?: () => void; // legacy until v2 sidebar is adopted
}

interface RefundTransactionFormValues {
    reason: string;
    revoke_tickets: boolean;
}

const RefundTransactionDialog = (props: RefundTransactionDialogProps) => {
    const { children, onSuccess, ...rest } = props;
    const toast = useToast();
    const { data: transaction } = useCurrentTransactionOrById();
    const transactionId = transaction?.id!;
    const tickets = transaction?.tickets ?? [];
    const { accountId } = useCurrentAccount();
    const { key, forceRender } = useForceRender();
    const methods = Form.useGivebutterForm<RefundTransactionFormValues>({
        defaultValues: {
            reason: '',
            revoke_tickets: true,
        },
    });
    const { control, handleSubmit, getValues, path, setError, resetFormButton } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: () => refundTransactionApi(accountId, transactionId, getValues()),
        onSuccess: () => {
            if (!onSuccess) {
                toast.success('Transaction refunded successfully!');
            }

            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });
            onSuccess?.();
            forceRender();
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;
            if (errors?.reason?.length) {
                setError('reason', {
                    message: errors.reason[0],
                });
            } else {
                toast.error(
                    <>
                        {error.response?.data.message ??
                            'Unknown error occurred while refunding transaction.'}
                    </>,
                );
            }
            resetFormButton();
        },
    });

    return (
        <AlertDialog.Root key={key} type="warning" {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <Form.GivebutterFormProvider {...methods}>
                    <form onSubmit={handleSubmit(() => submit())}>
                        <AlertDialog.Content>
                            <AlertDialog.Header>
                                <AlertDialog.Icon />
                                <AlertDialog.Close />
                            </AlertDialog.Header>
                            <AlertDialog.Body>
                                <AlertDialog.Title>Are you sure?</AlertDialog.Title>
                                <AlertDialog.Description>
                                    Refunds take 5-10 days to appear on a supporter's statement.
                                    We'll send an email confirmation to {transaction?.first_name}.
                                </AlertDialog.Description>
                                <Form.Container>
                                    <Form.Label name={path('reason')} className="mt-4 font-medium">
                                        Reason:
                                    </Form.Label>
                                    <Form.SingleSelect
                                        control={control}
                                        name="reason"
                                        rules={{ required: 'Reason is required' }}
                                    >
                                        <SingleSelect.Trigger aria-label="Reason" />
                                        <SingleSelect.Content>
                                            <SingleSelect.Item value={'duplicate'}>
                                                <SingleSelect.ItemText>
                                                    Duplicate charge
                                                </SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                            <SingleSelect.Item value={'fraud_admin'}>
                                                <SingleSelect.ItemText>Fraud</SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                            <SingleSelect.Item value={'customer_request'}>
                                                <SingleSelect.ItemText>
                                                    Requested by donor
                                                </SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                            <SingleSelect.Item value={'customer_unauthorized'}>
                                                <SingleSelect.ItemText>
                                                    Unauthorized charge
                                                </SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                            <SingleSelect.Item value={'customer_return'}>
                                                <SingleSelect.ItemText>
                                                    Item or ticket return
                                                </SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                        </SingleSelect.Content>
                                    </Form.SingleSelect>
                                    <Form.UseFormError name={path('reason')} />
                                </Form.Container>
                                {tickets.length > 0 && (
                                    <Form.Container className={'flex-row gap-2'}>
                                        <Form.UseFormCheckboxInput
                                            control={control}
                                            name={'revoke_tickets'}
                                            className={'mt-1'}
                                        />
                                        <Form.Label name={path('revoke_tickets')}>
                                            This transaction has a ticket associated with it. Would
                                            you like to also revoke the ticket?
                                        </Form.Label>
                                    </Form.Container>
                                )}
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                <Form.SubmitButton className="w-1/2">Refund</Form.SubmitButton>
                            </AlertDialog.Footer>
                        </AlertDialog.Content>
                    </form>
                </Form.GivebutterFormProvider>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const RefundTransactionDialogTrigger = AlertDialog.Trigger;

const Root = RefundTransactionDialog;
const Trigger = RefundTransactionDialogTrigger;

export {
    RefundTransactionDialog,
    RefundTransactionDialogTrigger,
    //
    Root,
    Trigger,
};
